import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Check, Mail } from "lucide-react";
import { getAllPieces, LIMITED_EDITIONS_URL } from "../data/site";
import OpenEditionsNotify from "../components/OpenEditionsNotify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: "collector-editions",
    label: "Collector Editions",
    tagline: "Signed • Numbered • Archival",
    price: "$150–$500+",
    blurb:
      "Hand-signed and numbered by the artist. Museum-grade cotton paper, archival pigment inks, shipped flat from the Denver studio with a certificate of authenticity.",
    cta: "Browse collector editions",
    testid: "tier-collector",
  },
  {
    id: "open-editions",
    label: "Open Editions",
    tagline: "Accessible • Multiple sizes",
    price: "$45–$195",
    blurb:
      "The same paintings at an everyday price point, in a range of sizes and paper stocks. Fulfilled automatically by Gelato and printed close to you.",
    cta: "Join the launch list",
    testid: "tier-open",
    badge: "Coming soon",
  },
];

export function Prints() {
  const [prints, setPrints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/prints`)
      .then((res) => {
        if (mounted) setPrints(res.data.prints || []);
      })
      .catch(() => toast.error("Couldn't load the print catalog."))
      .finally(() => mounted && setLoading(false));
    return () => (mounted = false);
  }, []);

  return (
    <div data-testid="prints-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">Two ways to collect</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Prints.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
          Every print starts as an original painting from the studio. Choose a
          signed collector edition, or an open edition sized for any wall.
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border" data-testid="print-tiers">
          {TIERS.map((t, i) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              className="relative bg-background p-8 md:p-12 hover:bg-[hsl(var(--surface))] transition-colors group"
              data-testid={t.testid}
            >
              {t.badge && (
                <span className="absolute top-6 right-6 md:top-8 md:right-8 inline-block bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
                  {t.badge}
                </span>
              )}
              <p className="label text-[hsl(var(--ink-soft))]">0{i + 1}</p>
              <h2 className="serif text-3xl md:text-5xl mt-4 tracking-tight font-light group-hover:italic transition-all">
                {t.label}
              </h2>
              <p className="label mt-4">{t.tagline}</p>
              <p className="serif text-3xl md:text-4xl mt-6">{t.price}</p>
              <p className="mt-5 text-sm md:text-base text-[hsl(var(--ink-soft))] leading-relaxed max-w-md">
                {t.blurb}
              </p>
              <span className="mt-8 inline-flex items-center gap-2 label group-hover:text-foreground">
                {t.cta} <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </a>
          ))}
        </div>
      </div>

      <section
        id="collector-editions"
        className="border-t border-border scroll-mt-28"
        data-testid="collector-editions"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="label mb-3">01 · Collector Editions</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl tracking-tight font-light leading-[1.02]">
                Signed. Numbered.{" "}
                <span className="italic text-[hsl(var(--ink-soft))]">Archival.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:text-right">
              <a
                href={LIMITED_EDITIONS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
                data-testid="prints-square-cta"
              >
                Shop all on Square
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <p className="mt-3 text-xs text-[hsl(var(--ink-soft))]">
                Secure checkout · Afterpay available
              </p>
            </div>
          </header>

          {loading ? (
            <p className="label">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
              {prints.map((p) => (
                <article key={p.id} className="art-card" data-testid={`print-${p.id}`}>
                  <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
                    <img src={p.image} alt={p.title} className="art-img w-full h-full object-cover" />
                    {!p.payment_link && (
                      <span className="absolute top-3 left-3 inline-block bg-[hsl(var(--ink-soft))] text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="label">Collector Edition</p>
                    <h3 className="serif text-2xl mt-1">{p.title}</h3>
                    {p.story && (
                      <p className="text-[hsl(var(--ink))]/85 italic mt-3 leading-relaxed text-[0.95rem]">
                        {p.story}
                      </p>
                    )}
                    <p className="text-sm text-[hsl(var(--ink-soft))] mt-3 leading-relaxed">
                      {p.description}
                    </p>
                    <div className="mt-5 flex items-center justify-between gap-3">
                      <span className="serif text-2xl">${p.price.toFixed(0)}</span>
                      {p.payment_link ? (
                        <a
                          href={p.payment_link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
                          data-testid={`print-buy-${p.id}`}
                        >
                          Buy via Square
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <Link
                          to={`/contact?print=${encodeURIComponent(p.title)}`}
                          className="inline-flex items-center gap-2 border border-foreground px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
                          data-testid={`print-inquire-${p.id}`}
                        >
                          <Mail className="h-3.5 w-3.5" />
                          Notify Me
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          <p className="mt-16 text-xs text-[hsl(var(--ink-soft))] max-w-2xl">
            Payments processed by Square. Orders, shipping, and receipts are
            handled directly by the studio's Square account — no account needed
            to purchase.
          </p>
        </div>
      </section>

      <section className="border-t border-border bg-[hsl(var(--surface))]" data-testid="prints-from-catalog">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-10 md:mb-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="label mb-3">By Painting</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl tracking-tight font-light leading-[1.02]">
                The paintings you can take home{" "}
                <span className="italic text-[hsl(var(--ink-soft))]">as a print.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-[hsl(var(--ink-soft))] leading-relaxed">
              Some are already released as collector editions, others are in
              production. Tap through to each painting for its story and to be
              notified when its edition drops.
            </p>
          </header>
          <PrintPickGrid />
          <div className="mt-12 md:mt-16 text-center border-t border-border pt-10 md:pt-14">
            <Link
              to="/collections/prints-available"
              className="inline-flex items-center gap-2 label link-underline"
              data-testid="prints-see-all-collection"
            >
              See every piece available as a print →
            </Link>
          </div>
        </div>
      </section>

      <section
        id="open-editions"
        className="border-t border-border bg-foreground text-background scroll-mt-28"
        data-testid="open-editions"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <p className="label mb-3 text-background/60">02 · Open Editions · Coming soon</p>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl tracking-tight font-light leading-[1.02]">
              Accessible. Multiple sizes.{" "}
              <span className="italic text-background/70">$45–$195.</span>
            </h2>
            <p className="mt-6 max-w-xl text-background/75 leading-relaxed">
              Open editions bring the paintings to every wall — smaller
              formats, more sizes, a lower price point. Fulfilled automatically
              by Gelato, printed at the facility nearest you, and shipped
              within days.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-background/70">
              {[
                "Fine-art paper and framed options",
                "Printed locally · lower shipping, lower footprint",
                "Collector editions above stay hand-signed by the studio",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Check className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--accent))]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <p className="label mb-4 text-background/60">Be first when they go live</p>
            <OpenEditionsNotify dark />
          </div>
        </div>
      </section>

      <section className="border-t border-border" data-testid="prints-shop-more">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="label mb-2">Also in the shop</p>
            <p className="serif text-2xl md:text-3xl tracking-tight font-light">
              Books, Black Cowboys Exist merch, and gift cards.
            </p>
          </div>
          <Link
            to="/shop"
            className="inline-flex items-center gap-3 border border-foreground px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
            data-testid="prints-to-shop"
          >
            Visit the shop <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function PrintPickGrid() {
  const eligible = getAllPieces()
    .filter(
      ({ meta }) => meta.prints_available === true || meta.prints_coming_soon === true
    )
    .sort((a, b) => {
      const rank = (m) => (m.prints_available ? 0 : 1);
      return rank(a.meta) - rank(b.meta);
    })
    .slice(0, 6);

  if (eligible.length === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
      {eligible.map(({ piece, meta, seriesSlug, seriesLabel }) => (
        <Link
          key={piece.id}
          to={`/work/${seriesSlug}/${piece.id}`}
          className="art-card block group"
          data-testid={`print-pick-${piece.id}`}
        >
          <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
            <img src={piece.image} alt={piece.title} className="art-img w-full h-full object-cover" />
            <span className="absolute top-3 left-3 inline-block bg-background text-foreground border border-foreground px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
              {meta.prints_available ? "Prints Available" : "Prints Coming Soon"}
            </span>
          </div>
          <div className="mt-3 flex items-baseline justify-between gap-4">
            <span className="serif italic text-lg md:text-xl">{piece.title}</span>
            <span className="label whitespace-nowrap">
              {meta.print_price ? `from $${meta.print_price}` : piece.year}
            </span>
          </div>
          <p className="label mt-1 text-[hsl(var(--ink-soft))]">
            {seriesLabel} · {piece.size}
          </p>
        </Link>
      ))}
    </div>
  );
}
